/**
 * Native iOS Biometric Authentication Bridge Module
 * Integrates FaceID / TouchID via WKScriptMessageHandler and coordinates authentication events.
 *
 * @module integrations/biometrics
 */

let _biometricLockEnabled = false;
let _isBiometricLocked = false;

/**
 * Checks whether native iOS biometric authentication is supported in the current environment.
 * @returns {boolean}
 */
export function isBiometricSupported() {
  return typeof window !== 'undefined' &&
    !!(window.webkit &&
       window.webkit.messageHandlers &&
       window.webkit.messageHandlers.biometricAuthHandler);
}

/**
 * Returns whether biometric lock is currently active.
 * @returns {boolean}
 */
export function isBiometricLocked() {
  return _isBiometricLocked;
}

/**
 * Sets whether the biometric app lock is currently active.
 * @param {boolean} locked
 */
export function setBiometricLocked(locked) {
  _isBiometricLocked = !!locked;
}

/**
 * Sets whether the biometric app lock is enabled on launch.
 * @param {boolean} enabled
 */
export function setBiometricLockEnabled(enabled) {
  _biometricLockEnabled = !!enabled;
}

/**
 * Returns whether the biometric app lock is enabled on launch.
 * @returns {boolean}
 */
export function getBiometricLockEnabled() {
  return _biometricLockEnabled;
}

/**
 * Triggers the native Face ID / Touch ID authorization prompt.
 * If running in a web browser without the native bridge, resolves gracefully or simulates.
 *
 * @param {Object} [options={}]
 * @param {boolean} [options.simulateInBrowser=false]
 * @returns {Promise<boolean>}
 */
export function triggerBiometricAuth(options = {}) {
  if (isBiometricSupported()) {
    try {
      window.webkit.messageHandlers.biometricAuthHandler.postMessage('requestBiometricAuth');
      return Promise.resolve(true);
    } catch (e) {
      console.warn('[Biometrics] Failed to post message to biometricAuthHandler:', e);
      return Promise.reject(e);
    }
  }

  // Browser fallback
  if (typeof window !== 'undefined') {
    if (options.simulateInBrowser || (typeof confirm === 'function' && confirm("Simulate successful biometric authentication for testing?"))) {
      if (typeof window.biometricAuthResult === 'function') {
        window.biometricAuthResult(true);
      }
      return Promise.resolve(true);
    } else {
      if (typeof window.biometricAuthResult === 'function') {
        window.biometricAuthResult(false, 'USER_CANCEL');
      }
      return Promise.resolve(false);
    }
  }

  return Promise.resolve(false);
}

export const requestBiometricAuth = triggerBiometricAuth;

/**
 * Initializes the biometric bridge callbacks on window and sets up event dispatching.
 *
 * @param {Object} [options={}]
 * @param {Function} [options.onAuthResult]
 */
export function initBiometricBridge(options = {}) {
  if (typeof window === 'undefined') return;

  const handleResult = (success, errorCode) => {
    const isSuccess = !!success;
    if (isSuccess) {
      _isBiometricLocked = false;
    }

    const event = new CustomEvent('pulsefit:biometric-status', {
      detail: {
        success: isSuccess,
        errorCode: errorCode || null,
        locked: _isBiometricLocked
      }
    });
    window.dispatchEvent(event);

    if (typeof options.onAuthResult === 'function') {
      options.onAuthResult(isSuccess, errorCode);
    }
  };

  // Bind global callbacks expected by Swift WKWebView
  window.biometricAuthResult = handleResult;
  window.handleBiometricResult = handleResult;
}
